<template>
  <div class="job-openings">
    <hero-section
      :title="title"
      :showWhiteWave="showWhiteWave"
      :description="description"
      :hasButton="hasButton"
      :navIndicator="navIndicator"
      :has-apply-button="true"
      :apply-button-url="buttonUrl"
      :apply-button-text="buttonText"
    />
    <div class="section__container">
      <div class="loader" v-if="UIController.loader.contentLoading">
        <v-progress-circular indeterminate></v-progress-circular>
        <p class="mt-5">Hold On! while we fetch the available job openings.</p>
      </div>
      <div v-else>
        <v-card elevation="0" class="error-card" v-if="jobOpenings.length < 1">
          <v-card-title class="error-card__title text-left dms700"
            >No Openings Available</v-card-title
          >
          <v-card-text>
            <h3 class="error-card__text-title text-left m500">
              Looks like there are no openings available at the moment
            </h3>
            <p class="error-card__text text-left m500">
              Why don't you check back in a day or two to see what we have got
              for you.
            </p>
          </v-card-text>
        </v-card>
        <div v-else>
          <a
            v-for="jobOpening in jobOpenings"
            :key="jobOpening.id"
            :href="`/talents-on-demand/${jobOpening.slug}`"
            class="job-card__link"
          >
            <v-card class="job-card" elevation="0">
              <v-card-text>
                <div class="job-card__header text-left">
                  <v-chip
                    size="small"
                    :color="jobOpening.department.color"
                  >{{ jobOpening.department.name }}</v-chip>
                </div>
                <p class="job-card__title text-left ">
                  {{ jobOpening.name }}
                </p>
                <p class="job-card__description text-left" v-html="jobOpening.roleSummary"></p>
                <div class="job-card__tags text-left">
                  <v-chip
                    v-for="hardSkill in jobOpening.hardSkills"
                    :key="hardSkill.id"
                    variant="outlined"
                    class="job-card__skill-chip mt-2 mb-2 mr-3"
                    size="small"
                    style="border-color: #7c7979;"

                  >
                    {{ hardSkill.label }}
                  </v-chip>
                </div>
                <div class="job-card__details text-left">
                  <p class="job-card__detail" v-if="jobOpening.employmentType">
                    <v-icon small class="mr-1 clock-icon">mdi-clock-outline</v-icon>
                    {{ jobOpening.employmentType }}
                  </p>
                  <p class="job-card__detail" v-if="jobOpening.workMode">
                    <v-icon small class="mr-1 clock-icon">mdi-radiobox-marked</v-icon>
                    {{ jobOpening.workmode }}
                  </p>
                  <p class="job-card__detail" v-if="jobOpening.location">
                    <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
                    {{ jobOpening.location }}
                  </p>
                </div>
              </v-card-text>
            </v-card>
          </a>
        </div>
        <!-- Pagination -->
        <div
          v-if="jobOpenings.length > 0 && totalJobs > limit"
          class="pagination-container"
        >
          <v-pagination
            v-model="page"
            :length="totalPages"
            active-color="#007AB2"
          ></v-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import HeroSectionVue from '@/views/jobOpenings/HeroSection.vue';
import jobOpeningService from '@/services/API/jobOpeningService';

export default {
  name: 'JobOpenings',
  components: {
    'hero-section': HeroSectionVue,
  },
  setup() {
    const page = ref(1);
    const limit = 8; // Matches backend's limit
    const totalJobs = ref(0);
    const jobOpenings = ref([]);
    const UIController = ref({
      loader: {
        contentLoading: true,
      },
    });

    const totalPages = computed(() => Math.ceil(totalJobs.value / limit));

    const fetchJobOpenings = async () => {
      try {
        UIController.value.loader.contentLoading = true;
        // Ensure loaderSection is hidden if it exists
        const loaderSection = document.getElementById('loaderSection');
        if (loaderSection) loaderSection.classList.add('d-none');

        // Send 0-based offset to match backend
        console.log('Fetching jobs for page:', page.value, 'offset:', page.value - 1);
        const response = await jobOpeningService.listAllJobOpenings({
          offset: page.value - 1, // Backend expects 'offset'
          limit,
        });

        console.log('API Response:', response);
        if (response && response.status === 200 && response.data && response.data.data) {
          jobOpenings.value = response.data.data.map((job) => ({
            ...job,
            location: job.location,
            employmentType: job.jobType?.name,
            workmode: job.workMode?.name,
          }));
          totalJobs.value = response.data.total || 0;
          console.log('Updated jobOpenings:', jobOpenings.value, 'totalJobs:', totalJobs.value);
        } else {
          jobOpenings.value = [];
          totalJobs.value = 0;
          console.log('No data returned from API');
        }
      } catch (error) {
        console.error('Error fetching job openings:', error);
        jobOpenings.value = [];
        totalJobs.value = 0;
      } finally {
        UIController.value.loader.contentLoading = false;
      }
    };

    // Watch page changes to fetch new data and scroll to top
    watch(page, async (newPage) => {
      console.log('Page changed to:', newPage);
      await fetchJobOpenings();
      window.scrollTo({ top: 0, behavior: 'auto' });
    });

    // Initial fetch
    fetchJobOpenings();

    return {
      page,
      limit,
      totalJobs,
      jobOpenings,
      UIController,
      totalPages,
      title: 'Talents on Demand',
      description:
        'Join our talent pool. Submit your resume. We will work on your behalf to find your next opportunity.',
      hasButton: true,
      buttonText: 'Submit Your Resume',
      buttonUrl: '/drop-your-info',
      showWhiteWave: true,
      navIndicator: [
        { text: 'Home', icon: 'fas fa-home', link: '/' },
        { text: 'Talents on Demand', link: '' },
      ],
    };
  },
};
</script>

<style scoped>
.job-openings{
  background-color: var(--light-1);
}
.section__container {
  
  max-width: 1100px;
}
.error-card {
  background-color: var(--light-1);
  max-width: 500px;
  margin-inline: auto;
  margin-block: 2rem;
}

.loader {
  padding-block: 5rem;
}

.job-card {
  margin-block: 2rem;
  border-bottom: 1px solid var(--gray-2);
  background: transparent;
  transition: transform 0.2s;
  padding-bottom: 1.5rem;
}

.job-card:last-child {
  /* border-bottom: none; */
}

.job-card:hover {
  transform: translateY(-2px);
}

.job-card__link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.job-card__header {
  margin-bottom: 1rem;
}

.job-card__title {
  font-size: clamp(20px, 5vw, 34px);
  line-height: 1.2;
  margin-block: 0.5rem;
  color: #00AEFF;
  font-weight: 600;
}

.job-card__tags {
  margin-block: 1rem;
}

.job-card__skill-chip {
  margin-right: 0.5rem;
}

.job-card__description {
  font-size: 0.9rem;
  line-height: 1.5rem;
  max-height: 4.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  margin-bottom: 1rem;
}

.job-card__details {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
}

.job-card__detail {
  font-size: 0.85rem;
  color: var(--gray-1);
  display: flex;
  align-items: center;
  margin: 0;
}

.pagination-container {
  text-align: center;
  margin: 2rem 0;
}

.v-pagination__item--is-active {
  background-color: #007AB2 !important;
}

@media only screen and (max-width: 410px) {
  .job-card__details {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
}
</style>